"use client";

import React, { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

export default function MouseTilt({ children, className = "" }: { children: React.ReactNode, className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  
  const xPct = useMotionValue(0);
  const yPct = useMotionValue(0);

  const xSpring = useSpring(xPct, { stiffness: 300, damping: 30 });
  const ySpring = useSpring(yPct, { stiffness: 300, damping: 30 });

  const rotateX = useTransform(ySpring, [-0.5, 0.5], ["12deg", "-12deg"]);
  const rotateY = useTransform(xSpring, [-0.5, 0.5], ["-12deg", "12deg"]);
  
  // Glare effect transforms
  const backgroundX = useTransform(xSpring, [-0.5, 0.5], ["0%", "100%"]);
  const backgroundY = useTransform(ySpring, [-0.5, 0.5], ["0%", "100%"]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    xPct.set(mouseX / rect.width - 0.5);
    yPct.set(mouseY / rect.height - 0.5);
  };

  const handleMouseLeave = () => {
    xPct.set(0);
    yPct.set(0);
  };

  return (
    <div style={{ perspective: "1200px" }} className={className}>
      <motion.div
        ref={ref}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{
          rotateX,
          rotateY,
          transformStyle: "preserve-3d",
          borderRadius: "inherit",
        }}
        className="w-full h-full cursor-pointer relative group"
      >
        <div style={{ transform: "translateZ(40px)", borderRadius: "inherit" }} className="w-full h-full relative overflow-hidden">
          {/* Glare effect */}
          <motion.div 
            className="pointer-events-none absolute inset-0 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{
              background: "radial-gradient(circle at center, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 60%)",
              left: backgroundX,
              top: backgroundY,
              transform: "translate(-50%, -50%)",
              width: "200%",
              height: "200%",
            }}
          />
          {children}
        </div>
      </motion.div>
    </div>
  );
}
